import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import TeacherAssignment, { TeacherAssignmentType } from "./TeacherAssignment";
import GradeAssignment, { GradeAssignmentType } from "./GradeAssignment";
import {
  IGradeTotalQuestions,
  ITeacherTotalQuestions,
  useFetchTotalQuestionsQuery,
} from "app/services/reports";

const customStyles = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: "20px 0",
    width: "100%",
    "@media print": {
      padding: 0,
      width: "100%",
    },
  },
  header: {
    color: "rgba(60, 180, 229, 1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    "& > span": {
      fontWeight: "bold",
    },
    "@media print": {
      breakInside: "avoid",
      pageBreakBefore: "avoid",
      pageBreakAfter: "avoid",
    },
  },
  statistics: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& > span": {
      padding: "10px 30px",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
};

interface IProps {}
const QuestionsStatistic = (props: IProps) => {
  const { t, i18n } = useTranslation();
  const [teacherAssignment, setTeacherAssignment] =
    useState<TeacherAssignmentType>([]);
  const [gradeAssignment, setGradeAssignment] = useState<GradeAssignmentType>(
    []
  );
  const { data: questionsData, isLoading: isLoadingQuestions } =
    useFetchTotalQuestionsQuery();

  useEffect(() => {
    if (questionsData) {
      const { _grades: gradesData, _teachersAssignment: teachersData } =
        questionsData._entity;

      let teacherRecords = generateTeachersRecords(teachersData, t);
      setTeacherAssignment(teacherRecords);

      let gradesRecords = generateGradsRecords(gradesData, t);
      setGradeAssignment(gradesRecords);
    }
  }, [questionsData, i18n.dir()]);

  return (
    <Typography component="div" sx={customStyles.root}>
      <Typography component="p" sx={customStyles.header}>
        <Typography component="span" sx={{ fontSize: "22px" }}>
          {t("Total Number Of Questions")}
        </Typography>
        <Typography component="span" sx={{ fontSize: "16px" }}>
          {t("For Lessons & Freeplay")}
        </Typography>
      </Typography>
      <TeacherAssignment
        data={teacherAssignment}
        isLoading={isLoadingQuestions}
      />
      <GradeAssignment data={gradeAssignment} isLoading={isLoadingQuestions} />
    </Typography>
  );
};

export default QuestionsStatistic;

function generateGradsRecords(
  gradesData: IGradeTotalQuestions[],
  t: (value: string) => string
): GradeAssignmentType {
  let gradesRecords: GradeAssignmentType = [];
  gradesData.forEach((item) => {
    gradesRecords.push([
      t(item._gradeName),
      item._lessonsCompleted ?? 0,
      item._lessonsQuestions ?? 0,
      item._freePlayQuestions ?? 0,
    ]);
  });
  return gradesRecords;
}

function generateTeachersRecords(
  teachersData: ITeacherTotalQuestions[],
  t: (value: string) => string
): TeacherAssignmentType {
  let teacherRecords: TeacherAssignmentType = [];
  let lessonsCount = 0;
  let questionsCount = 0;
  let freeplayCount = 0;

  teachersData.forEach((row) => {
    let gradesRecords: GradeAssignmentType = [];
    row._grades.forEach((item) => {
      lessonsCount += item._lessonsCompleted ?? 0;
      questionsCount += item._lessonsQuestions ?? 0;
      freeplayCount += item._freePlayQuestions ?? 0;
      gradesRecords.push([
        t(item._gradeName),
        item._lessonsCompleted ?? 0,
        item._lessonsQuestions ?? 0,
        item._freePlayQuestions ?? 0,
      ]);
    });
    teacherRecords.push([row._teacherName, gradesRecords]);
  });
  teacherRecords.push([
    t("Grand Total"),
    [["", lessonsCount, questionsCount, freeplayCount]],
  ]);
  return teacherRecords;
}
